import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import CssBaseline from '@mui/material/CssBaseline';
import Drawer from '@mui/material/Drawer';
import Toolbar from '@mui/material/Toolbar';
import { useNavigate } from 'react-router-dom';
import useMediaQuery from '@mui/material/useMediaQuery';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import { Link } from 'react-router-dom';
import "../styles/ResponsiveDrawer.css";

const drawerWidth = 250;

/* ──────────────────────────────────────────────
   Category & Motif Data
   ────────────────────────────────────────────── */
const categories = [
  {
    key: 'abstract',
    label: 'Abstrakt',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.53 16.122a3 3 0 00-5.78 1.128 2.25 2.25 0 01-2.4 2.245 4.5 4.5 0 008.4-2.245c0-.399-.078-.78-.22-1.128zm0 0a15.998 15.998 0 003.388-1.62m-5.043-.025a15.994 15.994 0 011.622-3.395m3.42 3.42a15.995 15.995 0 004.764-4.648l3.876-5.814a1.151 1.151 0 00-1.597-1.597L14.146 6.32a15.996 15.996 0 00-4.649 4.763m3.42 3.42a6.776 6.776 0 00-3.42-3.42" />
      </svg>
    ),
    subcategories: [
      { key: 'animals', label: 'Djur' },
      { key: 'humans', label: 'Människor' },
      { key: 'nature', label: 'Natur' },
      { key: 'creatures', label: 'Varelser' },
      { key: 'architecture', label: 'Arkitektur' },
    ],
  },
  {
    key: 'futuristic',
    label: 'Futuristisk',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5" />
      </svg>
    ),
    subcategories: [
      { key: 'animals', label: 'Djur' },
      { key: 'humans', label: 'Människor' },
      { key: 'nature', label: 'Natur' },
      { key: 'creatures', label: 'Varelser' },
      { key: 'architecture', label: 'Arkitektur' },
    ],
  },
  {
    key: 'pop-art',
    label: 'Pop-art',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
      </svg>
    ),
    subcategories: [
      { key: 'animals', label: 'Djur' },
      { key: 'humans', label: 'Människor' },
      { key: 'nature', label: 'Natur' },
      { key: 'creatures', label: 'Varelser' },
      { key: 'architecture', label: 'Arkitektur' },
    ],
  },
  {
    key: 'photography',
    label: 'Fotografi',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0z" />
      </svg>
    ),
    subcategories: [
      { key: 'animals', label: 'Djur' },
      { key: 'humans', label: 'Människor' },
      { key: 'nature', label: 'Natur' },
      { key: 'creatures', label: 'Varelser' },
      { key: 'architecture', label: 'Arkitektur' },
    ],
  },
  {
    key: 'illustration',
    label: 'Illustration',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" />
      </svg>
    ),
    subcategories: [
      { key: 'animals', label: 'Djur' },
      { key: 'humans', label: 'Människor' },
      { key: 'nature', label: 'Natur' },
      { key: 'creatures', label: 'Varelser' },
      { key: 'architecture', label: 'Arkitektur' },
    ],
  },
  {
    key: 'bohemian',
    label: 'Bohemian',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
      </svg>
    ),
    subcategories: [
      { key: 'animals', label: 'Djur' },
      { key: 'humans', label: 'Människor' },
      { key: 'nature', label: 'Natur' },
      { key: 'creatures', label: 'Varelser' },
      { key: 'architecture', label: 'Arkitektur' },
    ],
  },
  {
    key: 'fantasy',
    label: 'Fantasy',
    icon: (
      <svg xmlns="http://www.w3.org/2000/svg" className="w-[18px] h-[18px]" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.362 5.214A8.252 8.252 0 0112 21 8.25 8.25 0 016.038 7.048 8.287 8.287 0 009 9.6a8.983 8.983 0 013.361-6.867 8.21 8.21 0 003 2.48z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 18a3.75 3.75 0 00.495-7.467 5.99 5.99 0 00-1.925 3.546 5.974 5.974 0 01-2.133-1.001A3.75 3.75 0 0012 18z" />
      </svg>
    ),
    subcategories: [
      { key: 'animals', label: 'Djur' },
      { key: 'humans', label: 'Människor' },
      { key: 'nature', label: 'Natur' },
      { key: 'creatures', label: 'Varelser' },
      { key: 'architecture', label: 'Arkitektur' },
    ],
  },
];

const motifs = [
  { key: 'animals-1', label: 'Djur', icon: '🐾' },
  { key: 'humans-1', label: 'Människor', icon: '👤' },
  { key: 'vehicle-1', label: 'Fordon', icon: '🏍️' },
  { key: 'nature-1', label: 'Natur', icon: '🌿' },
];

/* ──────────────────────────────────────────────
   Chevron SVG
   ────────────────────────────────────────────── */
const ChevronDown = ({ isOpen }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className={`w-4 h-4 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
  </svg>
);


/* ══════════════════════════════════════════════
   SIDEBAR CONTENT
   ══════════════════════════════════════════════ */
function SidebarContent({ props, location, scrollToTop }) {
  const [expandedCategory, setExpandedCategory] = useState(null);

  const handleCategoryClick = (cat) => {
    // Toggle expand
    setExpandedCategory(expandedCategory === cat.key ? null : cat.key);
    // Reset filters
    props.setLoadCount(25);
    props.setScrollPosition(0);
    props.setPageFilter1(cat.key);
    props.setPageFilter2('');
    props.setImageDisplayUrls([]);
    props.setCheckout(false);
    scrollToTop();
  };

  const handleSubcategoryClick = (catKey, subKey) => {
    props.setLoadCount(25);
    props.setScrollPosition(0);
    props.setOpenSidebar(false);
    props.setPageFilter1(catKey);
    props.setPageFilter2(subKey);
    props.setCheckout(false);
    props.setImageDisplayUrls([]);
  };

  const handleMotifClick = () => {
    props.setLoadCount(25);
    props.setScrollPosition(0);
    props.setPageFilter1('');
    props.setPageFilter2('');
    props.setOpenSidebar(false);
  };

  const isActive = (path) => location.pathname === path || location.pathname === path + '/';

  return (
    <div className="h-full flex flex-col bg-white">
      {/* Spacer for fixed navbar */}
      <div className="h-[94px] flex-shrink-0" />

      {/* Scrollable content */}
      <div className="flex-1 overflow-y-auto scrollbar-hide px-3 pb-6">

        {/* ── Kategorier ── */}
        <div className="pt-4 pb-2">
          <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-[0.12em] px-2 mb-2">
            Kategorier
          </h3>
        </div>

        <div className="space-y-0.5">
          {categories.map((cat) => {
            const isExpanded = expandedCategory === cat.key;
            const isCatActive = location.pathname.startsWith(`/${cat.key}`);

            return (
              <div key={cat.key}>
                {/* Category button */}
                <Link to={`/${cat.key}/`} id="link" className="no-underline">
                  <button
                    onClick={() => handleCategoryClick(cat)}
                    className={`w-full flex items-center justify-between px-2.5 py-2 rounded-lg text-left border-none cursor-pointer transition-all duration-150
                      ${isCatActive
                        ? 'bg-gray-100 text-gray-900 font-semibold'
                        : 'bg-transparent text-gray-700 hover:bg-gray-50 font-medium'
                      }`}
                  >
                    <div className="flex items-center gap-2.5">
                      <span className={`flex-shrink-0 ${isCatActive ? 'text-brand-dark' : 'text-gray-400'}`}>
                        {cat.icon}
                      </span>
                      <span className="text-[13px]">{cat.label}</span>
                    </div>
                    <ChevronDown isOpen={isExpanded} />
                  </button>
                </Link>

                {/* Subcategories */}
                <div className={`overflow-hidden transition-all duration-200 ${isExpanded ? 'max-h-72' : 'max-h-0'}`}>
                  <div className="ml-5 pl-4 border-l border-gray-200 mt-0.5 mb-1 space-y-0.5">
                    {cat.subcategories.map((sub) => {
                      const subPath = `/${cat.key}/${sub.key}`;
                      const isSubActive = isActive(subPath);

                      return (
                        <Link to={subPath} id="link" key={sub.key} className="no-underline">
                          <button
                            onClick={() => handleSubcategoryClick(cat.key, sub.key)}
                            className={`w-full text-left px-2.5 py-1.5 rounded-md text-[12px] border-none cursor-pointer transition-colors duration-150
                              ${isSubActive
                                ? 'bg-brand-dark/5 text-brand-dark font-semibold'
                                : 'bg-transparent text-gray-500 hover:text-gray-900 hover:bg-gray-50 font-normal'
                              }`}
                          >
                            {sub.label}
                          </button>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Divider ── */}
        <div className="my-4 border-t border-gray-200" />

        {/* ── Motiv ── */}
        <div className="pb-2">
          <h3 className="text-[11px] font-bold text-gray-400 uppercase tracking-[0.12em] px-2 mb-2">
            Motiv
          </h3>
        </div>

        <div className="space-y-0.5">
          {motifs.map((motif) => {
            const motifPath = `/${motif.key}/`;
            const isMotifActive = location.pathname.startsWith(`/${motif.key}`);

            return (
              <Link to={motifPath} id="link" key={motif.key} className="no-underline">
                <button
                  onClick={handleMotifClick}
                  className={`w-full flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-left border-none cursor-pointer transition-all duration-150
                    ${isMotifActive
                      ? 'bg-gray-100 text-gray-900 font-semibold'
                      : 'bg-transparent text-gray-700 hover:bg-gray-50 font-medium'
                    }`}
                >
                  <span className="text-base flex-shrink-0 w-[18px] text-center">{motif.icon}</span>
                  <span className="text-[13px]">{motif.label}</span>
                </button>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}


/* ══════════════════════════════════════════════
   RESPONSIVE DRAWER
   ══════════════════════════════════════════════ */
function ResponsiveDrawer(props) {
  const isTabletView = useMediaQuery('(max-width:810px)');
  const location = useLocation();
  const navigate = useNavigate();

  const scrollToTop = () => {
    document.documentElement.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    if (props.displayStyle === 'abstract') navigate('/abstract/');
    else if (props.displayStyle === 'fantasy') navigate('/fantasy/');
    else if (props.displayStyle === 'illustration') navigate('/illustration/');
    else if (props.displayStyle === 'futuristic') navigate('/futuristic/');
    else if (props.displayStyle === 'pop-art') navigate('/pop-art/');
  }, [props.displayStyle]);

  const handleDrawerToggle = () => {
    props.setMenuOpen(!props.openSidebar);
    props.setOpenSidebar(!props.openSidebar);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    props.setSnackbarOpen(false);
  };

  const { window } = props;
  const container = window !== undefined ? () => window().document.body : undefined;

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />

      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
        aria-label="category navigation"
      >
        {/* Mobile drawer (temporary) */}
        <Drawer
          container={container}
          variant="temporary"
          open={props.openSidebar}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true, style: { zIndex: 2 } }}
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: 'none',
              boxShadow: '4px 0 24px rgba(0,0,0,0.08)',
            },
            zIndex: 1,
          }}
        >
          <SidebarContent props={props} location={location} scrollToTop={scrollToTop} />
        </Drawer>

        {/* Desktop drawer (permanent) */}
        <Drawer
          variant="permanent"
          sx={{
            display: isTabletView ? 'none' : 'block',
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              zIndex: 1,
              borderRight: '1px solid #f0f0f0',
              overflow: 'auto',
              scrollbarWidth: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            },
          }}
          open
        >
          <SidebarContent props={props} location={location} scrollToTop={scrollToTop} />
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{ flexGrow: 1, p: 3, width: { sm: `calc(100% - ${drawerWidth}px)` } }}
      >
        <Toolbar />
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={props.snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        style={{ top: '94px' }}
      >
        <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
          Poster tillagd i varukorgen!
        </Alert>
      </Snackbar>
    </Box>
  );
}

ResponsiveDrawer.propTypes = {
  window: PropTypes.func,
};

export default ResponsiveDrawer;
